/**
 * Excel Export Utility - Export professionale con ExcelJS
 */
import ExcelJS from 'exceljs';
import { ExportColumn, ExportConfig, SummaryRow, MultiSheetExportConfig } from './exportTypes';
import {
  EXPORT_CONFIG,
  generateFilename,
  formatDateForExport,
  formatCurrencyForExport,
  formatBooleanForExport,
} from './exportConfig';

/**
 * Ottiene il valore di un campo nested (es: "risorsa.nome")
 */
function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce((current, key) => {
    return current && typeof current === 'object' ? (current as Record<string, unknown>)[key] : undefined;
  }, obj as unknown);
}

/**
 * Formatta il valore in base al tipo di colonna
 */
function formatCellValue(value: unknown, type: ExportColumn['type']): string | number {
  if (value === null || value === undefined) return '';

  switch (type) {
    case 'date':
      return formatDateForExport(value as Date | string);
    case 'currency':
      return typeof value === 'number' ? value : parseFloat(String(value)) || 0;
    case 'number':
      return typeof value === 'number' ? value : parseFloat(String(value)) || 0;
    case 'boolean':
      return formatBooleanForExport(value as boolean);
    case 'percentage':
      return typeof value === 'number' ? value : parseFloat(String(value)) || 0;
    default:
      return String(value);
  }
}

/**
 * Applica lo stile all'header
 */
function applyHeaderStyle(cell: ExcelJS.Cell): void {
  cell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: EXPORT_CONFIG.colors.headerBg.replace('#', 'FF') },
  };
  cell.font = {
    name: EXPORT_CONFIG.excel.fontName,
    size: EXPORT_CONFIG.excel.headerFontSize,
    bold: true,
    color: { argb: EXPORT_CONFIG.colors.headerText.replace('#', 'FF') },
  };
  cell.alignment = { vertical: 'middle', horizontal: 'center' };
  cell.border = {
    top: { style: 'thin', color: { argb: EXPORT_CONFIG.colors.borderColor.replace('#', 'FF') } },
    bottom: { style: 'thin', color: { argb: EXPORT_CONFIG.colors.borderColor.replace('#', 'FF') } },
    left: { style: 'thin', color: { argb: EXPORT_CONFIG.colors.borderColor.replace('#', 'FF') } },
    right: { style: 'thin', color: { argb: EXPORT_CONFIG.colors.borderColor.replace('#', 'FF') } },
  };
}

/**
 * Applica lo stile alle celle dati
 */
function applyDataCellStyle(cell: ExcelJS.Cell, isAltRow: boolean, type: ExportColumn['type']): void {
  if (isAltRow) {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: EXPORT_CONFIG.colors.altRowBg.replace('#', 'FF') },
    };
  }

  cell.font = {
    name: EXPORT_CONFIG.excel.fontName,
    size: EXPORT_CONFIG.excel.fontSize,
  };

  cell.border = {
    top: { style: 'thin', color: { argb: EXPORT_CONFIG.colors.borderColor.replace('#', 'FF') } },
    bottom: { style: 'thin', color: { argb: EXPORT_CONFIG.colors.borderColor.replace('#', 'FF') } },
    left: { style: 'thin', color: { argb: EXPORT_CONFIG.colors.borderColor.replace('#', 'FF') } },
    right: { style: 'thin', color: { argb: EXPORT_CONFIG.colors.borderColor.replace('#', 'FF') } },
  };

  // Allineamento in base al tipo
  switch (type) {
    case 'currency':
    case 'number':
    case 'percentage':
      cell.alignment = { vertical: 'middle', horizontal: 'right' };
      break;
    case 'date':
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
      break;
    default:
      cell.alignment = { vertical: 'middle', horizontal: 'left' };
  }

  // Formato numerico per valute e percentuali
  if (type === 'currency') {
    cell.numFmt = '#,##0.00 €';
  } else if (type === 'percentage') {
    cell.numFmt = '#,##0.0"%"';
  } else if (type === 'number') {
    cell.numFmt = '#,##0.##';
  }
}

/**
 * Applica lo stile alla riga di riepilogo
 */
function applySummaryStyle(cell: ExcelJS.Cell): void {
  cell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: EXPORT_CONFIG.colors.summaryBg.replace('#', 'FF') },
  };
  cell.font = {
    name: EXPORT_CONFIG.excel.fontName,
    size: EXPORT_CONFIG.excel.fontSize,
    bold: true,
  };
  cell.border = {
    top: { style: 'thin', color: { argb: EXPORT_CONFIG.colors.borderColor.replace('#', 'FF') } },
    bottom: { style: 'thin', color: { argb: EXPORT_CONFIG.colors.borderColor.replace('#', 'FF') } },
    left: { style: 'thin', color: { argb: EXPORT_CONFIG.colors.borderColor.replace('#', 'FF') } },
    right: { style: 'thin', color: { argb: EXPORT_CONFIG.colors.borderColor.replace('#', 'FF') } },
  };
}

/**
 * Popola un foglio Excel con dati e formattazione
 */
function populateSheet(
  worksheet: ExcelJS.Worksheet,
  columns: ExportColumn[],
  rows: Record<string, unknown>[],
  title: string,
  summaryData?: SummaryRow[]
): void {
  const { excel, appName } = EXPORT_CONFIG;

  // Riga 1: Titolo
  const titleCell = worksheet.getCell('A1');
  titleCell.value = `${appName} - ${title}`;
  titleCell.font = {
    name: excel.fontName,
    size: excel.titleFontSize,
    bold: true,
    color: { argb: EXPORT_CONFIG.colors.textPrimary.replace('#', 'FF') },
  };
  worksheet.mergeCells(1, 1, 1, columns.length);

  // Riga 2: Data generazione
  const dateCell = worksheet.getCell('A2');
  dateCell.value = `Generato il: ${formatDateForExport(new Date())}`;
  dateCell.font = {
    name: excel.fontName,
    size: excel.fontSize,
    italic: true,
    color: { argb: EXPORT_CONFIG.colors.textSecondary.replace('#', 'FF') },
  };
  worksheet.mergeCells(2, 1, 2, columns.length);

  // Riga 3: vuota
  // Riga 4: Header
  const headerRowIndex = 4;
  const headerRow = worksheet.getRow(headerRowIndex);
  columns.forEach((col, index) => {
    const cell = headerRow.getCell(index + 1);
    cell.value = col.header;
    applyHeaderStyle(cell);
  });
  headerRow.height = excel.headerRowHeight;

  // Abilita filtri
  worksheet.autoFilter = {
    from: { row: headerRowIndex, column: 1 },
    to: { row: headerRowIndex, column: columns.length },
  };

  // Righe dati
  rows.forEach((row, rowIndex) => {
    const dataRow = worksheet.getRow(headerRowIndex + 1 + rowIndex);
    const isAltRow = rowIndex % 2 === 1;

    columns.forEach((col, colIndex) => {
      const cell = dataRow.getCell(colIndex + 1);
      const value = getNestedValue(row, col.field);
      cell.value = formatCellValue(value, col.type);
      applyDataCellStyle(cell, isAltRow, col.type);
    });

    dataRow.height = excel.dataRowHeight;
  });

  // Righe di riepilogo
  if (summaryData && summaryData.length > 0) {
    const summaryStartRow = headerRowIndex + 1 + rows.length + 1; // +1 per riga vuota

    // Il totale va incolonnato con gli importi quando la tabella ha una colonna valuta,
    // così il riepilogo si legge sotto i valori che riassume.
    const currencyIndex = columns.findIndex((col) => col.type === 'currency');

    summaryData.forEach((summary, index) => {
      const summaryRow = worksheet.getRow(summaryStartRow + index);
      const labelCell = summaryRow.getCell(1);
      labelCell.value = summary.label;
      applySummaryStyle(labelCell);

      // Merge le prime colonne per la label
      const valueColIndex = currencyIndex > 0 ? currencyIndex + 1 : Math.max(columns.length - 1, 2);
      if (valueColIndex > 1) {
        worksheet.mergeCells(summaryStartRow + index, 1, summaryStartRow + index, valueColIndex - 1);
      }

      const valueCell = summaryRow.getCell(valueColIndex);
      if (summary.type === 'currency' && typeof summary.value === 'number') {
        valueCell.value = summary.value;
        valueCell.numFmt = '#,##0.00 €';
      } else if (summary.type === 'number' && typeof summary.value === 'number') {
        valueCell.value = summary.value;
        valueCell.numFmt = '#,##0';
      } else if (summary.type === 'percentage' && typeof summary.value === 'number') {
        valueCell.value = summary.value;
        valueCell.numFmt = '#,##0.0"%"';
      } else {
        valueCell.value = typeof summary.value === 'number'
          ? formatCurrencyForExport(summary.value)
          : summary.value;
      }
      valueCell.alignment = { horizontal: 'right' };
      applySummaryStyle(valueCell);

      // Style per le celle intermedie
      for (let i = valueColIndex + 1; i <= columns.length; i++) {
        applySummaryStyle(summaryRow.getCell(i));
      }
    });
  }

  // Auto-fit colonne
  columns.forEach((col, index) => {
    const column = worksheet.getColumn(index + 1);
    const width = col.width || excel.defaultColumnWidth;
    column.width = Math.min(Math.max(width, excel.minColumnWidth), excel.maxColumnWidth);
  });
}

/**
 * Export singolo foglio Excel
 */
export async function exportToExcel(
  config: ExportConfig,
  data: Record<string, unknown>[]
): Promise<void> {
  if (data.length === 0) {
    throw new Error('Nessun dato da esportare');
  }

  const workbook = new ExcelJS.Workbook();
  workbook.creator = EXPORT_CONFIG.appName;
  workbook.created = new Date();

  const worksheet = workbook.addWorksheet(config.title, {
    pageSetup: {
      orientation: config.orientation || 'portrait',
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
    },
  });

  populateSheet(worksheet, config.columns, data, config.title, config.summaryData);

  // Download file
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  const filename = generateFilename(config.filename, 'xlsx');
  downloadBlob(blob, filename);
}

/**
 * Export multi-foglio Excel
 */
export async function exportToExcelMultiSheet(config: MultiSheetExportConfig): Promise<void> {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = EXPORT_CONFIG.appName;
  workbook.created = new Date();

  for (const sheet of config.sheets) {
    if (sheet.rows.length === 0) continue;

    const worksheet = workbook.addWorksheet(sheet.name, {
      pageSetup: {
        orientation: 'landscape',
        fitToPage: true,
        fitToWidth: 1,
        fitToHeight: 0,
      },
    });

    populateSheet(worksheet, sheet.columns, sheet.rows, sheet.title, sheet.summaryData);
  }

  // Download file
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  const filename = generateFilename(config.filename, 'xlsx');
  downloadBlob(blob, filename);
}

/**
 * Helper per scaricare un blob
 */
function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
